/**
 * CASEWORK STUDIO (2026-10-08, Matt: "design top-tier luxury casework … consistent with work by
 * some of the best in the interior design business"). Decisions (asked one at a time):
 * style lives on the RUN — every box follows it — and any single box can OVERRIDE any choice
 * (glass on two uppers, a different pull on the sink base). Changing the run's style moves
 * every box that has no override of its own.
 *
 * Storage — two instance params on every casework element, both kind 'data' (never a form
 * field, never carried by the eyedropper; declared in the casework schema so resolution
 * passes them through):
 *   runStyle  — { key: value } shared by every member of the run (written to all of them,
 *               exactly like runLengthFt)
 *   boxStyle  — { key: value } this box's own overrides
 *
 * Resolution — `foldCaseworkStyle` lays type → runStyle → boxStyle over the RESOLVED TYPE
 * PARAMS, so every existing consumer (the prototype builder, its instancing key, the counter
 * join, plan symbols, schedules) reads the effective value off `typeParams` with no change of
 * its own. The cabinet COLOR is the one exception: it is the instance `paintColor` (what the
 * Paint Brush writes, and what `memberColor` already resolves from) — a box's own brushed
 * color is its override, and the run's color fills in when the box has none.
 */
import type { ParamValue } from './types'

/** choices a RUN carries (and a box may override) — Studio stage 1: today's options */
export const RUN_STYLE_KEYS = [
  'frameMode', 'panelStyle', 'drawerPanelStyle', 'pullStyle', 'doorPullStyle', 'drawerPullStyle',
  'doorPullSizeIn', 'drawerPullSizeIn', 'twoPullsWide', 'hardwareFinish',
  'toeKick', 'baseStyle', 'baseProfile', 'footStyle', 'baseHeightIn', 'floatHeightIn', 'crownStyle', 'crownHeightIn', 'lightRail',
  'interiorFit', 'shelfCount', 'glassShelves', 'backPanel', 'drawerInsert',
  'pwrDrawer', 'pwrPopup', 'pwrInterior', 'plugMold',
  'ledUnder', 'puckUnder', 'ledInterior', 'ledToe', 'ledAbove', 'lightTempK',
  // stage 4: the countertop and backsplash
  'counterThicknessIn', 'counterEdge', 'counterFrontOverIn', 'counterEndOverIn', 'counterWaterfall',
  // COUNTER ENDS (2026-10-23): what each end of the top does — '' finds out (a wall there: stop
  // flush; nothing there: overhang), 'wall' always stops, 'open' always overhangs
  'counterStart', 'counterEnd',
  'counterSplash', 'splashMaterial',
  'surfaceMaterial', 'frontMaterial', 'carcassMaterial', 'counterMaterial', 'hardwareMaterial',
  'paintColor',
] as const

/** choices that belong to ONE box (its front layout) — overrides of the type, never run-wide */
export const BOX_LAYOUT_KEYS = ['frontConfig', 'drawerCount', 'hingeSide', 'drawerBand', 'drawerSizing', 'kneeApron', 'style', 'upperCell', 'floatShelfCount', 'appliance'] as const

/** a stored style map, or an empty one (anything else stored there is ignored) */
export function styleMapOf(v: unknown): Record<string, ParamValue> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, ParamValue>) : {}
}

const set = (v: unknown) => v !== null && v !== undefined

/**
 * Fold a casework element's run style and box overrides over its resolved params (mutates
 * both maps — they are the resolver's own fresh copies). Exported for the resolver + tests.
 */
export function foldCaseworkStyle(
  typeParams: Record<string, ParamValue>, params: Record<string, ParamValue>,
): void {
  const run = styleMapOf(params.runStyle)
  const box = styleMapOf(params.boxStyle)
  for (const src of [run, box]) {
    for (const [k, v] of Object.entries(src)) {
      if (k === 'paintColor' || !set(v)) continue
      typeParams[k] = v
    }
  }
  if (!set(params.paintColor) && set(run.paintColor)) params.paintColor = run.paintColor
  // door / drawer hardware follow the Pulls choice unless set; the base follows Toe Kick
  if (!typeParams.doorPullStyle) typeParams.doorPullStyle = typeParams.pullStyle || 'none'
  if (!typeParams.drawerPullStyle) typeParams.drawerPullStyle = typeParams.pullStyle || 'none'
  if (!typeParams.baseStyle) typeParams.baseStyle = typeParams.toeKick === false ? 'flush' : 'toe'
  // UPPER HEIGHT (2026-10-15): the palette's "Bottom at" reads the box's own override, else
  // blank (the type's mount height)
  params.upperBottomFt = typeParams.style === 'upper' && set(box.mountHeightFt) ? box.mountHeightFt : null
  // BOX DEPTH (2026-10-21, Matt: "every box can have an independent depth setting"): the type's
  // depth is only the DEFAULT a new box is drawn at; a box's own `boxStyle.depthFt` (folded onto
  // typeParams.depthFt above, which every consumer reads) wins. The palette's Depth reads it here.
  params.boxDepthFt = set(box.depthFt) ? box.depthFt : null
}

/** a casework element's effective depth off its STORED params + its type's params (for code
 *  that reads raw records, not resolved ones): the box's own depth, else the type default */
export function caseworkBoxDepthFt(params: Record<string, unknown>, typeParams: Record<string, unknown> | null | undefined): number {
  const box = styleMapOf(params.boxStyle)
  if (typeof box.depthFt === 'number' && box.depthFt > 0) return box.depthFt
  return typeof typeParams?.depthFt === 'number' ? typeParams.depthFt : 2
}


/** a frame mode that has a face frame (stiles a neighbour can share) */
export const hasFaceFrame = (mode: unknown) => mode === 'overlay' || mode === 'inset' || mode === 'beaded'

/** which keys this box overrides (its color counts when it has a brushed color of its own) */
export function boxOverrideKeys(params: Record<string, ParamValue>): string[] {
  const box = styleMapOf(params.boxStyle)
  const out = Object.keys(box).filter((k) => set(box[k]))
  if (set(params.paintColor) && !out.includes('paintColor')) out.push('paintColor')
  return out
}
